import { Dataset } from '../../model/dataset/dataset';
import { RenderingOptions } from '../../model/dataset/rendering/rendering-options';
import { ActivateOverlayEvent } from '../../model/events/activate-overlay-event';

/**
 * Dataset holding the bioclimatic zone boundaries. The climate rasters are meant to be
 * read against those lines, so it is switched on together with them.
 */
export const BIOCLIMATIC_ZONE_DATASET_ID = 'bioclimatic_zones_v2';

/** geoWorkspace the CMIP6 "change" datasets are published under. */
export const CLIMATE_CHANGE_GEO_WORKSPACE = 'climate_change';

/**
 * Fallback for accounts where those datasets are not published under the climate_change
 * geoWorkspace. The geoWorkspace check is the primary one, so new datasets are picked up
 * without touching this list.
 */
export const CLIMATE_CHANGE_DATASET_IDS: string[] = [
    'climate_change_cd_v2',      // Cooling Degree Days (CD)
    'climate_fd_change_v2',      // Frost days (FD)
    'climate_hd_change_v2',      // Heating Degree Days (HD)
    'climate_cdd_change_v2',     // Maximum Consecutive Dry Days (CDD)
    'climate_rx1day_change_v2',  // Max of 1-day accumulated precipitation (RX1day)
    'climate_rx5day_change_v2',  // Max of 5-day accumulated precipitation (RX5day)
    'climate_txx_change_v2',     // Max of daily maximum temperature (TXx)
    'climate_sst_change_v2',     // Mean Sea Surface Temperature (SST)
    'climate_pr_change_v2',      // Mean of daily accumulated precipitation (PR)
    'climate_prsn_change_v2',    // Mean of daily accumulated snowfall (PRSN)
    'climate_t_change_v2',       // Mean of daily mean temperature (T)
    'climate_sfcwind_change_v2', // Mean of daily mean wind speed (SFCWIND)
    'climate_tx35_change_v2',    // Days with maximum temperature above 35C (TX35)
    'climate_tx40_change_v2',    // Days with maximum temperature above 40C (TX40)
    'climate_slr_change_v2',     // Sea Level Rise
    'climate_spi6_change_v2',    // Standardized Precipitation Index (SPI-6)
    'climate_tr_change_v2'       // Tropical Night
];

/** The part of the overlays panel this sequence needs. */
export interface OverlayRenderingOptionsPanel {
    getOverlaysRenderingOptions(): Map<string, RenderingOptions>;
    setRenderingOption(dataset: Dataset): void;
}

export interface ClimateOverlaySequenceDeps {
    mainDatasetID: string;
    activeDatasetsOnMap: Dataset[];
    panel: OverlayRenderingOptionsPanel;
    getDataset(datasetID: string): Dataset;
    enable(event: ActivateOverlayEvent): Promise<any> | void;
    addFilterOverlay(datasetID: string): void;
    markSelected(datasetID: string): void;
    onSequenceComplete(): void;
}

/**
 * Set while the zone boundaries are on their way to the map. activeDatasetsOnMap is only
 * updated once the insert completes, so without this a second climate overlay ticked in
 * the meantime would add the zone a second time.
 */
let pendingZoneActivation: Promise<void> | null = null;

function isClimateChangeOverlay(dataset: Dataset): boolean {
    if (!dataset) {
        return false;
    }
    return dataset.geoWorkspace === CLIMATE_CHANGE_GEO_WORKSPACE
        || CLIMATE_CHANGE_DATASET_IDS.includes(dataset.id);
}

/**
 * Returns null when the zone overlay cannot or should not be activated: the account does
 * not have it, it is already on the map (and therefore already in front of anything
 * added now), or it has no rendering options - insertOverlay() requires them.
 */
function buildBioclimaticZoneEvent(deps: ClimateOverlaySequenceDeps): ActivateOverlayEvent | null {
    const zone = deps.getDataset(BIOCLIMATIC_ZONE_DATASET_ID);
    if (!zone || !deps.panel) {
        return null;
    }
    if (deps.activeDatasetsOnMap.some((dataset) => dataset.id === BIOCLIMATIC_ZONE_DATASET_ID)) {
        return null;
    }

    let renderingOptions = deps.panel.getOverlaysRenderingOptions().get(zone.id);
    if (!renderingOptions) {
        deps.panel.setRenderingOption(zone);
        renderingOptions = deps.panel.getOverlaysRenderingOptions().get(zone.id);
    }
    if (!renderingOptions) {
        return null;
    }

    zone.isSelected = true; // the state service copy, used when the map state is saved
    deps.markSelected(zone.id); // the tree copy, which is what the checkbox renders
    return { active: true, datasetID: zone.id, renderingOptions };
}

/**
 * Returns the promise that resolves once the zone boundaries are on the map, or null when
 * there is nothing to wait for - it is already there, or the account does not have it.
 *
 * Climate overlays ticked while an activation is in flight share that one activation, so
 * the zone is never added, or registered with the filter service, twice.
 */
function ensureBioclimaticZone(deps: ClimateOverlaySequenceDeps): Promise<void> | null {
    if (pendingZoneActivation) {
        return pendingZoneActivation;
    }

    const zoneEvent = buildBioclimaticZoneEvent(deps);
    if (!zoneEvent) {
        return null;
    }

    pendingZoneActivation = Promise.resolve(deps.enable(zoneEvent))
        .then(() => deps.addFilterOverlay(zoneEvent.datasetID))
        .catch(() => { /* the climate overlay must reach the map either way */ })
        .then(() => {
            pendingZoneActivation = null;
        });
    return pendingZoneActivation;
}

/**
 * Activates a CMIP6 "change" overlay together with the bioclimatic zone boundaries.
 *
 * Overlays are inserted at the bottom of overlayMapTypes, so whatever was activated first
 * stays in front. The zone boundaries therefore have to reach the map *before* the
 * climate raster, or the raster covers them - hence the chained activation.
 *
 * Returns false when the event is not a climate overlay, so the caller keeps its normal
 * path. An account without the zone dataset activates the raster exactly as before.
 */
export function activateClimateChangeOverlay(
    event: ActivateOverlayEvent,
    deps: ClimateOverlaySequenceDeps
): boolean {
    if (event.datasetID === deps.mainDatasetID) {
        return false; // the main dataset keeps its own insertion path
    }
    if (!isClimateChangeOverlay(deps.getDataset(event.datasetID))) {
        return false;
    }

    const zoneReady = ensureBioclimaticZone(deps);
    if (!zoneReady) {
        deps.enable(event);
        completeSequence(deps);
        return true;
    }

    zoneReady.then(() => {
        deps.enable(event);
        completeSequence(deps);
    });
    return true;
}

/**
 * Runs after the overlays are on the map, sometimes from a promise callback - by then the
 * map view may be gone, and that must not break the activation that already happened.
 */
function completeSequence(deps: ClimateOverlaySequenceDeps): void {
    try {
        deps.onSequenceComplete();
    } catch {
        // switching the map view is cosmetic, the overlays are what matter
    }
}
